import {VIEWER_QUERY_KEY_MAP} from '../api/viewer/constant';
import {requestContext} from '../common/requestContext';
import {IS_CLIENT_SIDE} from '../common/shared/utils/constants';
import {isTrue} from '../common/shared/utils/isTrue';
import {events} from '../domain/content/[seriesId]/viewer/[productId]/constant/talkViewerEvents';
import {
  changeViewingMode,
  clearTalkViewerError,
  forceProcessToTalkIndex,
  setIsMediaAutoPlay as setIsMediaAutoPlayAction,
  setIsSettingMenuExposed as setIsSettingMenuExposedAction,
  setMuted as setMutedAction,
  setViewerMenuHeight as setViewerMenuHeightAction,
  talkViewerErrorOccured,
} from '../domain/content/[seriesId]/viewer/[productId]/state/talkViewerSlice';
import {setIsViewerMenuExposed} from '../domain/content/[seriesId]/viewer/[productId]/state/viewerMenuSlice';
import {ViewerMenuHeight} from '../domain/content/[seriesId]/viewer/[productId]/type/ViewerMenuHeight.interface';
import {ViewingMode} from '../domain/content/[seriesId]/viewer/[productId]/type/ViewingMode.enum';
import {fetchTalkViewerContent} from '../domain/content/[seriesId]/viewer/[productId]/util/fetchTalkViewerContent.thunk';
import {fetchViewerEnd} from '../domain/content/[seriesId]/viewer/[productId]/util/fetchViewerEnd';
import {TalkViewerErrorType} from '../errors/TalkViewerErrorType.enum';
import {TalkViewerData, ViewerInfo} from '../graphql/generated/types';
import {createQueryClient} from '../reactQuery/createQueryClient';
import {AppDispatch, AppStore} from '../state/store';

/**
 * 톡뷰어 웹 핸들러를 주입한다. \
 * Arguments 는 외부(app, 테스트페이지 등)에서 함수 호출 시 string 으로 넘어올 수도 있으니 string 이 아닐 경우 안전하게 Type Conversion 해준다.
 */
export function initKnWeb(store: AppStore) {
  if (!IS_CLIENT_SIDE || 'KNWeb' in window) {
    return;
  }

  function setViewerMenuExposed(_exposed: boolean) {
    const exposed = isTrue(_exposed);
    store.dispatch(
      setIsViewerMenuExposed({isExposed: exposed, isInvokedByApp: true})
    );
  }
  function setViewingMode(viewingMode: 'scroll' | 'tap') {
    const talkViewerState = store.getState().talkViewer;
    // 종종 앱에서 같은 값으로의 viewingModeChange를 시도하는 경우가 있다. ( ex. 네이티브 플레이어가 닫히고 웹뷰로 돌아올 때 )
    // 비정상적인 상황이고, changeViewingMode saga가 실행되면 안 되는 상황이므로 early return 해줌
    if (talkViewerState.settings.viewingMode === viewingMode) {
      return;
    }
    store.dispatch(changeViewingMode(viewingMode as ViewingMode));
  }
  function setIsMediaAutoPlay(_autoPlay: boolean) {
    const autoPlay = isTrue(_autoPlay);
    store.dispatch(setIsMediaAutoPlayAction(autoPlay));
  }
  function setMuted(muted: boolean) {
    store.dispatch(setMutedAction(isTrue(muted)));
  }
  function setViewerMenuHeight(viewerMenuHeight: ViewerMenuHeight) {
    if (!viewerMenuHeight || typeof viewerMenuHeight !== 'object') {
      throw new Error(
        'KNWeb.setViewerMenuHeight error. Param should be object'
      );
    }
    store.dispatch(setViewerMenuHeightAction(viewerMenuHeight));
  }
  function pauseViewer() {
    store.dispatch(events.viewerPaused());
  }
  function goToTalk(_talkIndex: number) {
    const talkIndex = Number(_talkIndex);
    store.dispatch(forceProcessToTalkIndex(talkIndex));
  }
  function backButtonClicked() {
    const talkViewerState = store.getState().talkViewer;
    if (talkViewerState.fullscreen) {
      store.dispatch(events.fullscreenHideRequested());
      return true;
    }
    return false;
  }
  function getCurrentTalkCount() {
    const talkViewerState = store.getState().talkViewer;
    const currentProductId = talkViewerState.currentProductId;

    const progress =
      currentProductId &&
      talkViewerState.productInfoMap[currentProductId].progress;
    return progress ? progress.visitedTalkCount : 0;
  }

  function getMuted() {
    const talkViewerState = store.getState().talkViewer;
    return talkViewerState.internalSettings.muted;
  }
  function reload() {
    const userState = store.getState().user;
    const talkViewerState = store.getState().talkViewer;
    // 앱인데 로그인을 할 수 없는 상황이라면 어떤 처리도 할 수 없으므로 에러를 발생시킵니다.
    if (!userState.user && requestContext.requestInfo.display.isWebView) {
      store.dispatch(talkViewerErrorOccured(TalkViewerErrorType.NeedAppLogin));
      return;
    }

    // 에러가 발생해서 재시도하는 경우 에러를 제거해서 boundary에서 재시도
    if (talkViewerState.error) {
      store.dispatch(clearTalkViewerError());
      return;
    }

    // 그 외는 강제로 데이터를 새로 불러옵니다.
    const viewerInfoState = store.getState().viewerInfo;
    const {currentSeriesId, currentProductId} = viewerInfoState;
    if (!currentProductId || !currentSeriesId) {
      return;
    }

    const queryClient = createQueryClient();
    const viewerInfo = queryClient.getQueryData<ViewerInfo>(
      VIEWER_QUERY_KEY_MAP.viewerInfo({
        seriesId: currentSeriesId,
        productId: currentProductId,
      })
    );
    if (!viewerInfo) {
      return;
    }

    const talkViewerData = viewerInfo?.viewerData as TalkViewerData;
    const decryptKey = talkViewerData.talkDownloadData.dec ?? undefined;
    const dispatch: AppDispatch = store.dispatch;
    dispatch(
      fetchTalkViewerContent({
        productId: currentProductId,
        triggerType: 'reload',
        decryptKey,
        viewerInfoData: viewerInfo,
      })
    );
    const appViewerEndPath = window?.KPApp?.getViewerEndPath?.();
    fetchViewerEnd({
      seriesId: currentSeriesId,
      productId: currentProductId,
      apiPath: appViewerEndPath,
    });
  }
  function showReloadPage() {
    store.dispatch(talkViewerErrorOccured(TalkViewerErrorType.AppError));
  }
  function closeVideoPlayer() {
    const talkViewerState = store.getState().talkViewer;
    if (talkViewerState.fullscreen) {
      store.dispatch(events.fullscreenHideRequested());
    }
  }
  function tapBackground() {
    store.dispatch(events.backgroundTapPressed());
  }
  function setIsSettingMenuExposed(isExposed: boolean) {
    store.dispatch(setIsSettingMenuExposedAction(!!isExposed));
  }

  window.KNWeb = {
    setViewerMenuExposed,
    setViewingMode,
    setIsMediaAutoPlay,
    setViewerMenuHeight,
    setMuted,
    pauseViewer,
    goToTalk,
    backButtonClicked,
    getCurrentTalkCount,
    getMuted,
    reload,
    showReloadPage,
    closeVideoPlayer,
    tapBackground,
    setIsSettingMenuExposed,
  };
}
